import type { WorkAreaGateType } from '@/utils/workAreaPermissionGate';
import { englishT } from '@/i18n/LocaleProvider';
import { pageT } from '@/i18n/pageTranslator';

/**
 * Why this session may not create a work area, or `null` when it may ask (I-0353, AC-U). A
 * verification session's ceiling has no `work_area.create` in it; where the work area may go and the
 * plan's limit are the server's to answer, inside the dialog.
 *
 * One reading for every entry: the sidebar's controls, the empty state's button, the phone menu's
 * row and the first screen's offer (AC-19.11).
 */
export function createWorkAreaRefusal(
  authStore: { isVerificationSession?: boolean } | null | undefined,
): string | null {
  // I-2183: from the catalogue, in the reader's language (the sidebar's words, drawn at launch).
  return authStore?.isVerificationSession
    ? pageT(englishT)('navigation.sidebar.verificationNoWorkArea')
    : null;
}

/** The same answer as a gate, for `gateControlProps`. */
export function createWorkAreaGate(
  authStore: { isVerificationSession?: boolean } | null | undefined,
): WorkAreaGateType {
  const reason = createWorkAreaRefusal(authStore);
  return reason ? { state: 'refused', reason } : { state: 'permitted', basis: 'granted' };
}

/**
 * Why this session may not create an organisation, or `null` (I-2184, AC-F of
 * `docs/concept-independent-verification-access.md`: every mutating control a verification session
 * can reach is refused with a readable reason). The sidebar's *New organisation* only opens Global
 * settings, where the flow starts; it is refused here, before that.
 */
export function createOrganisationRefusal(
  authStore: { isVerificationSession?: boolean } | null | undefined,
): string | null {
  return authStore?.isVerificationSession
    ? pageT(englishT)('navigation.sidebar.verificationNoOrganisation')
    : null;
}

/** The same answer as a gate, for `gateControlProps`. */
export function createOrganisationGate(
  authStore: { isVerificationSession?: boolean } | null | undefined,
): WorkAreaGateType {
  const reason = createOrganisationRefusal(authStore);
  return reason ? { state: 'refused', reason } : { state: 'permitted', basis: 'granted' };
}
